<template>
  <div class="space-y-2 relative">
    <!-- Endpoint search / select -->
    <div>
      <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{{
        tt('Z-Wave endpoint', 'Končna točka Z-Wave')
      }}</label>
      <select
        v-model="selectedEndpointId"
        class="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        @change="onEndpointChange">
        <option value="">{{ tt('Select endpoint…', 'Izberite končno točko…') }}</option>
        <option v-for="ep in endpoints" :key="ep.id" :value="ep.id">
          {{ tt('Node', 'Vozlišče') }} {{ ep.nodeId }}, {{ tt('Endpoint', 'Končna točka') }} {{ ep.endpointIndex }}
          <template v-if="ep.label"> – {{ ep.label }}</template>
        </option>
      </select>
    </div>

    <!-- Value picker -->
    <div v-if="selectedEndpointId && zwaveValues.length > 0">
      <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{{
        tt('Property', 'Lastnost')
      }}</label>
      <select
        v-model="selectedValueId"
        class="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        @change="emitValue">
        <option value="">{{ tt('Select property…', 'Izberite lastnost…') }}</option>
        <optgroup v-for="cc in commandClasses" :key="cc.ccId" :label="cc.ccName">
          <option v-for="val in cc.values" :key="val.id" :value="val.id">
            {{ val.label || val.propertyName }}
            <template v-if="val.unit"> ({{ val.unit }})</template>
            <template v-if="!val.isWritable && referenceType === 'control'">
              {{ tt(' [read-only]', ' [samo za branje]') }}</template
            >
          </option>
        </optgroup>
      </select>
    </div>

    <!-- Empty / loading states -->
    <div v-if="loading" class="text-xs text-gray-400 flex items-center gap-1">
      <span class="material-symbols-outlined text-sm animate-spin">progress_activity</span>
      {{ tt('Loading Z-Wave endpoints…', 'Nalaganje končnih točk Z-Wave…') }}
    </div>
    <div v-else-if="!loading && endpoints.length === 0" class="text-xs text-gray-400">
      {{ tt('No Z-Wave endpoints found for this integration.', 'Za to integracijo ni najdenih končnih točk Z-Wave.') }}
    </div>
    <div v-else-if="selectedEndpointId && zwaveValues.length === 0" class="text-xs text-gray-400">
      {{ tt('No properties for this endpoint.', 'Za to končno točko ni lastnosti.') }}
    </div>

    <!-- Selected value summary -->
    <div
      v-if="selectedValue"
      class="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700">
      <span class="material-symbols-outlined text-sm text-purple-500">electrical_services</span>
      <div class="flex-1 min-w-0">
        <div class="text-xs font-medium text-gray-900 dark:text-white">
          {{ selectedValue.label || selectedValue.propertyName }}
        </div>
        <div class="text-xs text-gray-500 dark:text-gray-400 font-mono">
          {{ selectedValue.dataType }}{{ selectedValue.unit ? ` · ${selectedValue.unit}` : '' }}
        </div>
      </div>
      <span
        :class="[
          'px-1.5 py-0.5 text-xs rounded',
          selectedValue.isWritable
            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
        ]">
        {{ selectedValue.isWritable ? tt('writable', 'za pisanje') : tt('read-only', 'samo za branje') }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { apiFetch } from '../services/api';
import { tt } from '../utils/consoleText';

export interface ZWaveValueRef {
  zwaveValueId: string; // ZWaveValue.id (UUID)
  zwaveEndpointId: string; // ZWaveEndpoint.id (UUID)
  propertyName: string;
  ccId: number;
  ccName: string;
  label?: string;
  unit?: string;
}

interface ZWaveEndpointItem {
  id: string;
  nodeId: number;
  endpointIndex: number;
  label?: string;
}

interface ZWaveValueItem {
  id: string;
  propertyName: string;
  ccId: number;
  ccName: string;
  label?: string;
  dataType: string;
  unit?: string;
  isWritable: boolean;
  endpointId?: string | number;
}

interface CommandClassGroup {
  ccId: number;
  ccName: string;
  values: ZWaveValueItem[];
}

interface Props {
  /** The hub stores the ids first and the picker fills the row in. */
  modelValue?: Partial<ZWaveValueRef>;
  integrationId: string;
  referenceType: 'status' | 'control';
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:modelValue': [value: ZWaveValueRef | undefined];
}>();

const loading = ref(false);
const endpoints = ref<ZWaveEndpointItem[]>([]);
const zwaveValues = ref<ZWaveValueItem[]>([]);
const selectedEndpointId = ref('');
const selectedValueId = ref('');

const commandClasses = computed((): CommandClassGroup[] => {
  const groups = new Map<number, CommandClassGroup>();
  for (const val of zwaveValues.value) {
    if (!groups.has(val.ccId)) {
      groups.set(val.ccId, { ccId: val.ccId, ccName: val.ccName, values: [] });
    }
    groups.get(val.ccId)!.values.push(val);
  }
  return Array.from(groups.values()).sort((a, b) => a.ccName.localeCompare(b.ccName));
});

const selectedValue = computed(() => zwaveValues.value.find((v) => v.id === selectedValueId.value));

async function loadEndpoints() {
  if (!props.integrationId) return;
  loading.value = true;
  try {
    const data = await apiFetch(`/integrations/${props.integrationId}/datapoints`);
    endpoints.value = Array.isArray(data?.zwave?.endpoints) ? data.zwave.endpoints : [];
  } catch (e) {
    console.error('[ZWaveReferenceInput] load error:', e);
  } finally {
    loading.value = false;
  }
}

async function onEndpointChange() {
  selectedValueId.value = '';
  zwaveValues.value = [];
  if (!selectedEndpointId.value) return;
  loading.value = true;
  try {
    const data = await apiFetch(`/integrations/${props.integrationId}/datapoints`);
    const allValues: ZWaveValueItem[] = Array.isArray(data?.zwave?.values) ? data.zwave.values : [];
    zwaveValues.value = allValues.filter((v) => v.endpointId === selectedEndpointId.value);
  } catch (e) {
    console.error('[ZWaveReferenceInput] load values error:', e);
  } finally {
    loading.value = false;
  }
}

function emitValue() {
  if (!selectedEndpointId.value || !selectedValueId.value) {
    emit('update:modelValue', undefined);
    return;
  }
  const val = selectedValue.value;
  if (!val) {
    emit('update:modelValue', undefined);
    return;
  }
  emit('update:modelValue', {
    zwaveValueId: val.id,
    zwaveEndpointId: selectedEndpointId.value,
    propertyName: val.propertyName,
    ccId: val.ccId,
    ccName: val.ccName,
    label: val.label,
    unit: val.unit,
  });
}

watch(
  () => props.modelValue,
  async (val) => {
    if (!val) {
      selectedEndpointId.value = '';
      selectedValueId.value = '';
      return;
    }
    await loadEndpoints();
    selectedEndpointId.value = val.zwaveEndpointId ?? '';
    await onEndpointChange();
    selectedValueId.value = val.zwaveValueId ?? '';
  },
  { immediate: true },
);

onMounted(() => {
  loadEndpoints();
});
</script>
